// src/pages/reports/InventoryReports.js
// v2 — Fixed 07 Jun 2026
// FIX: Deduplicate inventory_summary docs by item_id/item_name
//      inventory_summary has dual-format docs (old ITEM-XXXXX + new companyId_ITEM-XXXXX_random)
//      Same item appears multiple times — keep doc with highest stock

import { useEffect, useState, useCallback } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";

export default function InventoryReports() {
  const user      = JSON.parse(localStorage.getItem("user") || "{}");
  const companyId = user.company_id || "";

  const [inventoryData,  setInventoryData]  = useState([]);
  const [summary,        setSummary]        = useState({
    totalItems: 0, totalStockQty: 0,
    totalInventoryValue: 0, lowStockItems: 0,
  });
  const [loading,        setLoading]        = useState(true);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [stockFilter,    setStockFilter]    = useState("");

  const fetchInventoryReports = useCallback(async () => {
    try {
      setLoading(true);
      const invSnap = await getDocs(
        query(collection(db, "inventory_summary"), where("company_id", "==", companyId))
      );

      // ── STEP 1: map all docs ───────────────────────────────────────────
      const rawList = invSnap.docs.map(d => {
        const item     = { id: d.id, ...d.data() };
        const stock    = Number(item.qty || item.current_stock || item.available_qty || 0);
        const minStock = Number(item.minimum_stock || 5);
        const lastRate = Number(item.last_rate || 0);
        return { ...item, stock, minStock, lastRate };
      });

      // ── STEP 2: deduplicate by item_id, fallback to item_name ─────────
      // Multiple docs exist for same item (dual-format legacy + new format)
      // Rule: keep the doc with the highest stock value
      const dedupMap = new Map();

      // NEW — inactive items excluded
for (const item of rawList) {
  if (item.is_active === false) continue;                // ← NEW

  const key = item.item_id
    ? item.item_id
    : (item.item_name || "").toLowerCase().trim();

  if (!key) continue;

  const existing = dedupMap.get(key);
  if (!existing || item.stock > existing.stock) {
    dedupMap.set(key, item);
  }
}

      // ── STEP 3: compute summaries on deduped list ─────────────────────
      let totalItems = 0, totalStockQty = 0, totalInventoryValue = 0, lowStockItems = 0;

      const list = Array.from(dedupMap.values()).map(item => {
        const itemValue = item.stock * item.lastRate;
        const isLow     = item.stock <= item.minStock;

        totalItems++;
        totalStockQty       += item.stock;
        totalInventoryValue += itemValue;
        if (isLow) lowStockItems++;

        return { ...item, isLow, itemValue };
      }).sort((a, b) => b.stock - a.stock);

      setInventoryData(list);
      setSummary({ totalItems, totalStockQty, totalInventoryValue, lowStockItems });

    } catch (err) {
      console.error("Inventory reports error:", err);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId) fetchInventoryReports();
  }, [companyId, fetchInventoryReports]);

  const categories = [...new Set(inventoryData.map(i => i.category).filter(Boolean))];

  const filtered = inventoryData.filter(item => {
    if (categoryFilter && item.category !== categoryFilter) return false;
    if (stockFilter === "low"     && !item.isLow) return false;
    if (stockFilter === "healthy" &&  item.isLow) return false;
    return true;
  });

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold">Inventory Reports</h1>

      {/* SUMMARY */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Total Items"       value={summary.totalItems}                     color="blue"  />
        <StatCard title="Total Stock Qty"   value={summary.totalStockQty.toLocaleString()} color="blue"  />
        <StatCard
          title="Inventory Value"
          value={summary.totalInventoryValue > 0
            ? `₹${summary.totalInventoryValue.toLocaleString("en-IN")}`
            : "—"}
          color="blue"
        />
        <StatCard title="Low Stock Items"   value={summary.lowStockItems}
          color={summary.lowStockItems > 0 ? "red" : "green"} />
      </div>

      {/* FILTERS */}
      <div className="flex gap-3 flex-wrap">
        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
          className="border border-slate-200 p-2 rounded-xl text-sm bg-white outline-none">
          <option value="">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="flex gap-2">
          {[
            { val: "",        label: "All"           },
            { val: "low",     label: "🔴 Low Stock"  },
            { val: "healthy", label: "🟢 Healthy"    },
          ].map(({ val, label }) => (
            <button key={val} onClick={() => setStockFilter(val)}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition ${
                stockFilter === val
                  ? "bg-slate-800 text-white"
                  : "bg-white border border-slate-200 text-slate-600"
              }`}>{label}</button>
          ))}
        </div>
        <div className="ml-auto text-sm text-slate-500 self-center">
          {filtered.length} items
        </div>
      </div>

      {/* TABLE */}
      <div className="bg-white rounded-xl shadow p-4">
        <h2 className="text-lg font-semibold mb-4">Inventory Summary</h2>
        {loading ? (
          <p className="text-slate-400 text-sm">Loading...</p>
        ) : filtered.length === 0 ? (
          <p className="text-slate-400 text-sm">No items found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm border-collapse">
              <thead>
                <tr className="bg-slate-800 text-white">
                  <th className="p-3 text-left">Item</th>
                  <th className="p-3 text-left">Category</th>
                  <th className="p-3 text-right">Stock</th>
                  <th className="p-3 text-right">Last Rate</th>
                  <th className="p-3 text-right">Value</th>
                  <th className="p-3 text-right">Min Stock</th>
                  <th className="p-3 text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(item => (
                  <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="p-3 font-medium">{item.item_name || "-"}</td>
                    <td className="p-3 text-slate-500">{item.category || "-"}</td>
                    <td className="p-3 text-right font-semibold">{item.stock}</td>
                    <td className="p-3 text-right text-slate-400">
                      {item.lastRate > 0 ? `₹${item.lastRate.toLocaleString("en-IN")}` : "—"}
                    </td>
                    <td className="p-3 text-right font-semibold text-green-700">
                      {item.itemValue > 0 ? `₹${item.itemValue.toLocaleString("en-IN")}` : "—"}
                    </td>
                    <td className="p-3 text-right text-slate-400">{item.minStock}</td>
                    <td className="p-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        item.isLow
                          ? "bg-red-100 text-red-700"
                          : "bg-green-100 text-green-700"
                      }`}>
                        {item.isLow ? "Low Stock" : "Healthy"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* INSIGHTS */}
      <div className="bg-white rounded-xl shadow p-4">
        <h2 className="text-lg font-semibold mb-3">Inventory Insights</h2>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-500">Low Stock Risk Items</span>
            <span className="font-semibold text-red-600">{summary.lowStockItems}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Stock Health</span>
            <span className="font-semibold">
              {summary.totalItems > 0
                ? `${(((summary.totalItems - summary.lowStockItems) / summary.totalItems) * 100).toFixed(0)}% healthy`
                : "-"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, color = "blue" }) {
  const colors = {
    blue:  "bg-blue-50  border-blue-200",
    green: "bg-green-50 border-green-200",
    red:   "bg-red-50   border-red-200",
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[color] || colors.blue}`}>
      <p className="text-xs text-slate-500 uppercase tracking-wide">{title}</p>
      <h3 className="text-xl font-bold mt-1">{value}</h3>
    </div>
  );
}